import React from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Typography, 
  Divider,
  IconButton,
  Chip,
  Paper
} from '@mui/material';
import { 
  LocationOn as LocationIcon,
  Visibility as ViewIcon,
  Diamond as DiamondIcon,
  Watch as WatchIcon,
  Favorite as RingIcon
} from '@mui/icons-material';
import { useAssets } from '../../contexts/AssetsContext';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to get icon based on asset type
const getAssetIcon = (type) => {
  switch (type) {
    case 'necklace':
    case 'earrings':
      return <DiamondIcon />;
    case 'watch':
      return <WatchIcon />;
    case 'ring':
    case 'bracelet':
      return <RingIcon />;
    default:
      return <DiamondIcon />;
  }
};

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const AssetsListMobile = ({ onSelectAsset }) => {
  const { assets, getUserAssets } = useAssets();
  const { currentUser } = useAuth();
  
  // Get assets for the current user
  const userAssets = currentUser ? getUserAssets(currentUser.id) : assets;
  
  // If no assets, show a message
  if (userAssets.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No assets found. Add your first asset to start tracking.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ pb: 7 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            My Assets ({userAssets.length})
          </Typography>
        </Box>
        
        <List disablePadding>
          {userAssets.map((asset, index) => (
            <React.Fragment key={asset.id}>
              <ListItem 
                button 
                onClick={() => onSelectAsset(asset.id)}
                secondaryAction={
                  <IconButton edge="end" onClick={() => onSelectAsset(asset.id)}>
                    <ViewIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: asset.status === 'normal' ? 'success.50' : 'warning.50',
                      color: asset.status === 'normal' ? 'success.main' : 'warning.main'
                    }}
                  >
                    {getAssetIcon(asset.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {asset.name}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={asset.status.toUpperCase()} 
                        color={asset.status === 'normal' ? 'success' : 'warning'}
                        sx={{ ml: 1, height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {asset.type.charAt(0).toUpperCase() + asset.type.slice(1)} • {formatCurrency(asset.value)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <LocationIcon fontSize="small" sx={{ color: 'primary.main', mr: 0.5, fontSize: 16 }} />
                        <Typography variant="caption" color="text.secondary">
                          Last updated: {new Date(asset.lastUpdated).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
              {index < userAssets.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default AssetsListMobile;
